import { createSignal, Show, For, onMount } from 'solid-js'
import { useNavigate } from '@solidjs/router'
import { useAuth } from '../lib/auth'
import { scanBadge, getActiveCheckIns, createCheckIn, confirmCheckIn, checkOut, adjustCheckInItems, getVenue } from '../lib/api'
import { type CameraScanHandle, canUseCamera, startQrCamera } from '../lib/qr-camera'

type StaffView = 'scan' | 'guest' | 'checkin' | 'checkout' | 'active'

export default function Staff() {
  const navigate = useNavigate()
  const auth = useAuth()

  const [view, setView] = createSignal<StaffView>('scan')
  const [scannedGuest, setScannedGuest] = createSignal<{
    guestId: string; guestName: string; balance: number; activeCheckIns: number
    checkInId?: string | null
  } | null>(null)
  const [pendingRequests, setPendingRequests] = createSignal<Array<{
    id: string; guestId: string; guestName: string; totalAmount: number; createdAt: string
  }>>([])
  const [activeCheckIns, setActiveCheckIns] = createSignal<Array<{
    id: string; guestId: string; guestName: string;
    items: Array<{ id: string; type: string; tagNumber: number; status: string }>;
    totalAmount: number; createdAt: string;
  }>>([])
  const [selectedCheckIn, setSelectedCheckIn] = createSignal<string | null>(null)
  /** Camera problems are shown inline: the manual code field is the fallback. */
  const [error, setError] = createSignal<string | null>(null)
  const [itemCount, setItemCount] = createSignal(1)
  const [isProcessing, setIsProcessing] = createSignal(false)
  const [scanInput, setScanInput] = createSignal('')
  const [paidBy, setPaidBy] = createSignal('guest')
  const [adjustType, setAdjustType] = createSignal('')

  onMount(() => {
    if (!auth.isAuthenticated() || !auth.hasStaffRole()) {
      navigate('/signup')
      return
    }
    loadActiveCheckIns()
    void (async () => {
      const venue = auth.activeVenue()
      if (!venue) return
      const v = await getVenue(venue.venueId)
      const first = (v.data?.itemTypes ?? [])[0]?.name
      if (first) setAdjustType(first)
    })()
  })

  async function loadActiveCheckIns() {
    if (!auth.activeVenue()) return
    const res = await getActiveCheckIns(auth.activeVenue()!.venueId)
    if (res.data) {
      setActiveCheckIns(res.data.checkIns)
      setPendingRequests(res.data.pending ?? [])
    }
  }

  async function handleScan(explicit?: string) {
    const code = explicit ?? scanInput()
    if (!code) return
    setIsProcessing(true)
    const res = await scanBadge(code)
    if (res.data) {
      setScannedGuest(res.data)
      if (res.data.checkInId) {
        // The guest already asked for a check-in (their QR carried the request),
        // so the counter's job is to confirm it and hand over the tags.
        await confirmCheckIn(res.data.checkInId)
        await loadActiveCheckIns()
        setView('checkout')
      } else if (res.data.activeCheckIns > 0) {
        setView('checkout')
        await loadActiveCheckIns()
      } else {
        setView('guest')
      }
    }
    setIsProcessing(false)
  }

  async function handleCheckIn() {
    if (!scannedGuest() || !auth.activeVenue()) return
    setIsProcessing(true)
    const items = Array.from({ length: itemCount() }, (_, i) => ({ type: 'item' }))
    // A walk-in: the staff knows who they scanned, so the check-in belongs to them.
    const res = await createCheckIn(auth.activeVenue()!.venueId, items, scannedGuest()!.guestId)
    if (res.data) {
      await confirmCheckIn(res.data.checkInId)
    }
    setIsProcessing(false)
    setView('scan')
    setScanInput('')
    setScannedGuest(null)
    await loadActiveCheckIns()
  }

  // ─── Camera scanning ────────────────────────────────────────
  // Camera scanning. BarcodeDetector when the platform has it (Chrome/Android),
  // jsQR otherwise — without the fallback, an iPhone can never scan, because iOS
  // Safari has no BarcodeDetector at all.
  const [scanning, setScanning] = createSignal(false)
  let videoEl: HTMLVideoElement | undefined
  let cameraHandle: CameraScanHandle | null = null

  const supportsCameraScan = () => canUseCamera()

  async function startCameraScan() {
    if (!videoEl) {
      // Unreachable once the video is always mounted; say so rather than doing nothing.
      setError('Camera view is not ready — reload the page and try again.')
      return
    }
    setError(null)
    // Show the preview before the stream arrives: a `hidden` element has zero size
    // and some browsers will not decode frames into a display:none video.
    setScanning(true)
    const handle = await startQrCamera({
      video: videoEl,
      onResult: (value) => {
        cameraHandle = null
        setScanning(false)
        setScanInput(value)
        void handleScan(value)
      },
      onError: (message) => {
        cameraHandle = null
        setScanning(false)
        setError(message)
      },
    })
    if (handle) cameraHandle = handle
    else setScanning(false)
  }

  function stopCameraScan() {
    cameraHandle?.stop()
    cameraHandle = null
    setScanning(false)
  }

  async function handleConfirmRequest(sessionId: string) {
    setIsProcessing(true)
    await confirmCheckIn(sessionId)
    await loadActiveCheckIns()
    setIsProcessing(false)
    setView('scan')
    setScanInput('')
    setScannedGuest(null)
  }

  /** RETURN / ADJUST ITEMS — add or remove an item on an open check-in. */
  async function handleAdjust(checkInId: string, change: { add?: Array<{ type: string }>; removeItemIds?: string[] }) {
    setIsProcessing(true)
    await adjustCheckInItems(checkInId, change)
    await loadActiveCheckIns()
    setIsProcessing(false)
  }

  async function handleCheckOut(checkInId: string) {
    setIsProcessing(true)
    await checkOut(checkInId)
    await loadActiveCheckIns()
    setIsProcessing(false)
    setView('scan')
    setScanInput('')
    setScannedGuest(null)
  }

  return (
    <div class="min-h-dvh bg-gray-50 flex flex-col">
      {/* Header */}
      <header class="bg-white border-b border-gray-200 px-4 py-3">
        <div class="max-w-md mx-auto flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500">Staff Mode</p>
            <p class="font-semibold text-gray-900">{auth.activeVenue()?.venueName}</p>
          </div>
          <button onClick={() => { auth.switchMode('guest'); navigate('/pass') }}
            class="text-sm text-emerald-600 hover:text-emerald-700">
            Exit staff mode
          </button>
        </div>
      </header>

      <main class="flex-1 p-4">
        <div class="max-w-md mx-auto">
          {/* Scan View */}
          <Show when={view() === 'scan'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
              <h1 class="text-xl font-bold text-gray-900 mb-2">Scan badge</h1>
              <p class="text-sm text-gray-600 mb-4">
                Scan the guest's ZipPass or enter their code.
              </p>

              {/* Camera scan area */}
              <div class="relative w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center mb-4 border-2 border-dashed border-gray-300 overflow-hidden">
                {/*
                  The <video> is ALWAYS mounted, and only its visibility depends on
                  `scanning`. It used to sit inside this <Show>, so when "Open
                  camera" was tapped the ref was still undefined and startCameraScan()
                  returned early — silently, so the button appeared dead.
                */}
                <video
                  ref={(el) => (videoEl = el)}
                  class={`absolute inset-0 w-full h-full object-cover ${scanning() ? '' : 'hidden'}`}
                  playsinline
                  muted
                />
                <Show
                  when={scanning()}
                  fallback={
                    <div class="text-center px-4">
                      <p class="text-sm text-gray-500 mb-2">
                        {supportsCameraScan()
                          ? 'Point the camera at the guest\u2019s QR'
                          : 'This browser cannot open the camera — enter the code below'}
                      </p>
                      <Show when={supportsCameraScan()}>
                        <button
                          onClick={() => void startCameraScan()}
                          class="py-2 px-4 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700"
                        >
                          Open camera
                        </button>
                      </Show>
                    </div>
                  }
                >
                  <button
                    onClick={stopCameraScan}
                    class="absolute top-2 right-2 py-1 px-3 bg-black/60 text-white text-xs rounded-lg z-10"
                  >
                    Stop
                  </button>
                </Show>
              </div>

              <div class="flex gap-2">
                <input
                  type="text"
                  placeholder="Or enter code manually"
                  value={scanInput()}
                  onInput={(e) => setScanInput(e.currentTarget.value)}
                  class="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-base
                         focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <button
                  onClick={() => void handleScan()}
                  disabled={isProcessing() || !scanInput()}
                  class="py-3 px-6 bg-emerald-600 text-white font-semibold rounded-lg
                         hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                >
                  {isProcessing() ? '...' : 'Scan'}
                </button>
              </div>
            </div>

            {/* Guests waiting for the counter (check-in requests) */}
            <Show when={pendingRequests().length > 0}>
              <div class="bg-white rounded-xl shadow-sm border border-amber-200 p-4 mb-4">
                <h2 class="text-sm font-semibold text-amber-800 mb-3">
                  Waiting for you ({pendingRequests().length})
                </h2>
                <div class="space-y-2">
                  <For each={pendingRequests()}>
                    {(req) => (
                      <div class="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <div>
                          <p class="text-sm font-medium text-gray-900">{req.guestName}</p>
                          <p class="text-xs text-gray-500">
                            asked {new Date(req.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            · €{(req.totalAmount / 100).toFixed(2)}
                          </p>
                        </div>
                        <button
                          onClick={() => handleConfirmRequest(req.id)}
                          disabled={isProcessing()}
                          class="py-2 px-4 bg-emerald-600 text-white text-sm font-semibold rounded-lg
                                 hover:bg-emerald-700 disabled:opacity-50"
                        >
                          Confirm
                        </button>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            </Show>

            {/* Active items at venue */}
            <Show when={activeCheckIns().length > 0}>
              <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <h2 class="text-sm font-semibold text-gray-700 mb-3">
                  Active items ({activeCheckIns().length})
                </h2>
                <div class="space-y-2">
                  <For each={activeCheckIns()}>
                    {(ci) => (
                      <button
                        onClick={() => { setSelectedCheckIn(ci.id); setScannedGuest({ guestId: ci.guestId, guestName: ci.guestName, balance: 0, activeCheckIns: 1 }); setView('checkout') }}
                        class="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 text-left"
                      >
                        <div>
                          <p class="text-sm font-medium text-gray-900">{ci.guestName}</p>
                          <p class="text-xs text-gray-500">{ci.items.length} item{ci.items.length !== 1 ? 's' : ''}</p>
                        </div>
                        <span class="text-xs text-gray-400">
                          {new Date(ci.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </button>
                    )}
                  </For>
                </div>
              </div>
            </Show>
          </Show>

          {/* Guest identified — choose check-in */}
          <Show when={view() === 'guest' && scannedGuest()}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <button onClick={() => { setView('scan'); setScanInput('') }}
                class="text-sm text-gray-500 hover:text-gray-700 mb-4">← Back</button>

              <div class="text-center mb-6">
                <div class="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span class="text-2xl font-bold text-emerald-700">{scannedGuest()!.guestName.charAt(0)}</span>
                </div>
                <h1 class="text-xl font-bold text-gray-900">{scannedGuest()!.guestName}</h1>
                <p class="text-sm text-gray-500">Balance: €{(scannedGuest()!.balance / 100).toFixed(2)}</p>
              </div>

              <label class="block text-sm font-medium text-gray-700 mb-1">Number of items</label>
              <div class="flex items-center gap-3 mb-6">
                <button onClick={() => setItemCount(Math.max(1, itemCount() - 1))}
                  class="w-12 h-12 bg-gray-100 rounded-lg text-xl font-bold hover:bg-gray-200">−</button>
                <span class="text-2xl font-bold w-12 text-center">{itemCount()}</span>
                <button onClick={() => setItemCount(itemCount() + 1)}
                  class="w-12 h-12 bg-gray-100 rounded-lg text-xl font-bold hover:bg-gray-200">+</button>
              </div>

              <button onClick={handleCheckIn} disabled={isProcessing()}
                class="w-full py-4 bg-emerald-600 text-white font-bold rounded-lg text-lg
                       hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                {isProcessing() ? 'Processing...' : `Check in ${itemCount()} item${itemCount() !== 1 ? 's' : ''}`}
              </button>
            </div>
          </Show>

          {/* Checkout view */}
          <Show when={view() === 'checkout' && scannedGuest()}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <button onClick={() => { setView('scan'); setScanInput(''); setScannedGuest(null) }}
                class="text-sm text-gray-500 hover:text-gray-700 mb-4">← Back</button>

              <h1 class="text-xl font-bold text-gray-900 mb-1">{scannedGuest()!.guestName}</h1>
              <p class="text-sm text-gray-500 mb-4">Items to return:</p>

              <div class="space-y-2 mb-6">
                <For each={activeCheckIns().filter(ci => ci.guestId === scannedGuest()!.guestId).flatMap(ci => ci.items)}>
                  {(item) => (
                    <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p class="text-sm font-medium text-gray-900">{item.type} #{item.tagNumber}</p>
                      </div>
                      <button
                        onClick={() => {
                          const checkIn = activeCheckIns().find((ci) => ci.guestId === scannedGuest()!.guestId)
                          if (checkIn) handleAdjust(checkIn.id, { removeItemIds: [item.id] })
                        }}
                        disabled={isProcessing()}
                        class="text-xs text-red-600 hover:text-red-700 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </For>
              </div>

              <div class="mb-4 space-y-2">
                <button
                  onClick={() => {
                    const checkIn = activeCheckIns().find((ci) => ci.guestId === scannedGuest()!.guestId)
                    if (checkIn && adjustType()) handleAdjust(checkIn.id, { add: [{ type: adjustType() }] })
                  }}
                  disabled={isProcessing() || !adjustType()}
                  class="w-full py-2 px-3 bg-white text-gray-800 border border-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  + Add another item {adjustType() ? `(${adjustType()})` : ''}
                </button>
                <label class="block text-xs text-gray-500">Settled by</label>
                <select
                  value={paidBy()}
                  onChange={(e) => setPaidBy(e.currentTarget.value)}
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="guest">The guest</option>
                  <option value="venue">The venue</option>
                  <option value="UBER">Sponsor — UBER pays</option>
                </select>
              </div>

              <button
                onClick={() => {
                  const checkIn = activeCheckIns().find(ci => ci.guestId === scannedGuest()!.guestId)
                  if (checkIn) handleCheckOut(checkIn.id)
                }}
                disabled={isProcessing()}
                class="w-full py-4 bg-emerald-600 text-white font-bold rounded-lg text-lg
                       hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {isProcessing() ? 'Processing...' : 'Release all items'}
              </button>
            </div>
          </Show>
        </div>
      </main>
    </div>
  )
}
